import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["queueItem", "details", "toggleText", "markPostedForm", "postUrl", "snoozeForm"]

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  async copyCaption(event) {
    const button = event.currentTarget
    const caption = button.dataset.caption

    if (!caption) return

    try {
      await navigator.clipboard.writeText(caption)
      const originalHTML = button.innerHTML
      button.innerHTML = `<svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>Copied!`
      button.classList.remove("bg-[#C4A484]", "hover:bg-[#B39474]")
      button.classList.add("bg-[#9CAF88]")

      setTimeout(() => {
        button.innerHTML = originalHTML
        button.classList.remove("bg-[#9CAF88]")
        button.classList.add("bg-[#C4A484]", "hover:bg-[#B39474]")
      }, 2000)
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement("textarea")
      textarea.value = caption
      textarea.style.position = "fixed"
      textarea.style.opacity = "0"
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)

      const originalHTML = button.innerHTML
      button.innerHTML = `<svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>Copied!`
      setTimeout(() => { button.innerHTML = originalHTML }, 2000)
    }
  }

  async copySoundName(event) {
    const sound = event.currentTarget.dataset.sound
    if (!sound) return

    try {
      await navigator.clipboard.writeText(sound)
      const btn = event.currentTarget
      const original = btn.textContent
      btn.textContent = "Copied!"
      setTimeout(() => { btn.textContent = original }, 2000)
    } catch (err) {
      // Fallback
      const textarea = document.createElement("textarea")
      textarea.value = sound
      textarea.style.position = "fixed"
      textarea.style.opacity = "0"
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
  }

  toggleDetails(event) {
    const contentId = event.currentTarget.dataset.contentId
    const details = this.detailsTargets.find(el => el.dataset.contentId === contentId)
    const toggleText = this.toggleTextTargets.find(el => el.dataset.contentId === contentId)

    if (details) {
      details.classList.toggle("hidden")
      if (toggleText) {
        toggleText.textContent = details.classList.contains("hidden") ? "Show Details" : "Hide Details"
      }
    }
  }

  showMarkPosted(event) {
    const contentId = event.currentTarget.dataset.contentId
    const form = this.markPostedFormTargets.find(el => el.dataset.contentId === contentId)
    if (form) form.classList.remove("hidden")
  }

  hideMarkPosted(event) {
    const contentId = event.currentTarget.dataset.contentId
    const form = this.markPostedFormTargets.find(el => el.dataset.contentId === contentId)
    if (form) form.classList.add("hidden")
  }

  async confirmMarkPosted(event) {
    const contentId = event.currentTarget.dataset.contentId
    const clientId = event.currentTarget.dataset.clientId
    const postUrlInput = this.postUrlTargets.find(el => el.dataset.contentId === contentId)
    const postUrl = postUrlInput?.value || ""

    const button = event.currentTarget
    button.disabled = true
    button.textContent = "Saving..."

    try {
      const response = await fetch(`/sm/clients/${clientId}/content/${contentId}/mark_posted`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ post_url: postUrl })
      })

      if (response.ok) {
        // Slide out animation
        const queueItem = this.queueItemTargets.find(el => el.dataset.contentId === contentId)
        if (queueItem) {
          queueItem.style.transition = "all 0.3s ease-out"
          queueItem.style.transform = "translateX(100%)"
          queueItem.style.opacity = "0"
          queueItem.style.maxHeight = queueItem.offsetHeight + "px"
          setTimeout(() => {
            queueItem.style.maxHeight = "0"
            queueItem.style.padding = "0"
            queueItem.style.margin = "0"
            queueItem.style.border = "none"
          }, 300)
          setTimeout(() => {
            queueItem.remove()
            this.updateQueueCount()
          }, 600)
        }
      } else {
        button.disabled = false
        button.textContent = "Confirm Posted"
      }
    } catch (err) {
      button.disabled = false
      button.textContent = "Confirm Posted"
    }
  }

  showSnooze(event) {
    const contentId = event.currentTarget.dataset.contentId
    const form = this.snoozeFormTargets.find(el => el.dataset.contentId === contentId)
    if (form) {
      form.classList.toggle("hidden")

      // Reset custom datetime picker if exists
      const customSection = form.querySelector('[data-custom-datetime]')
      if (customSection) customSection.classList.add("hidden")
    }
  }

  showCustomDatetime(event) {
    const contentId = event.currentTarget.dataset.contentId
    const form = this.snoozeFormTargets.find(el => el.dataset.contentId === contentId)
    if (form) {
      const customSection = form.querySelector('[data-custom-datetime]')
      if (customSection) {
        customSection.classList.remove("hidden")
        const input = customSection.querySelector("input")
        if (input) input.focus()
      }
    }
  }

  async snooze(event) {
    const button = event.currentTarget
    const contentId = button.dataset.contentId
    const clientId = button.dataset.clientId
    const snoozeOption = button.dataset.snooze

    button.disabled = true
    const originalText = button.textContent
    button.textContent = "Snoozing..."

    try {
      const response = await fetch(`/sm/clients/${clientId}/content/${contentId}/snooze`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          snooze_option: snoozeOption
        })
      })

      const data = await response.json()

      if (data.success) {
        const queueItem = this.queueItemTargets.find(el => el.dataset.contentId === contentId)
        if (queueItem) {
          queueItem.style.transition = "all 0.3s ease-out"
          queueItem.style.transform = "translateX(100%)"
          queueItem.style.opacity = "0"
          setTimeout(() => {
            queueItem.remove()
            this.updateQueueCount()
          }, 300)
        }
      } else {
        button.textContent = originalText
        button.disabled = false
        alert(data.error || "Failed to snooze")
      }
    } catch (err) {
      button.textContent = originalText
      button.disabled = false
      console.error("Snooze error:", err)
    }
  }

  async snoozeCustom(event) {
    const button = event.currentTarget
    const contentId = button.dataset.contentId
    const clientId = button.dataset.clientId
    const form = this.snoozeFormTargets.find(el => el.dataset.contentId === contentId)
    const datetimeInput = form.querySelector('input[type="datetime-local"]')
    const customDatetime = datetimeInput?.value

    if (!customDatetime) {
      alert("Please select a date and time")
      return
    }

    button.disabled = true
    button.textContent = "Snoozing..."

    try {
      const response = await fetch(`/sm/clients/${clientId}/content/${contentId}/snooze`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          snooze_option: "custom",
          custom_datetime: customDatetime
        })
      })

      const data = await response.json()

      if (data.success) {
        const queueItem = this.queueItemTargets.find(el => el.dataset.contentId === contentId)
        if (queueItem) {
          queueItem.style.transition = "all 0.3s ease-out"
          queueItem.style.transform = "translateX(100%)"
          queueItem.style.opacity = "0"
          setTimeout(() => {
            queueItem.remove()
            this.updateQueueCount()
          }, 300)
        }
      } else {
        button.textContent = "Reschedule"
        button.disabled = false
        alert(data.error || "Failed to snooze")
      }
    } catch (err) {
      button.textContent = "Reschedule"
      button.disabled = false
      console.error("Snooze error:", err)
    }
  }

  updateQueueCount() {
    const remaining = this.queueItemTargets.length
    const badge = this.element.querySelector("span.rounded-full")
    if (badge) {
      badge.textContent = remaining
    }
    // Hide the whole section if empty
    if (remaining === 0) {
      this.element.style.transition = "all 0.3s ease-out"
      this.element.style.opacity = "0"
      setTimeout(() => { this.element.remove() }, 300)
    }
  }
}
